import React, { useState, useEffect, useCallback } from 'react';
import {
  Modal,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Card,
  CardBody
} from '@heroui/react';
import { MapPin, Navigation } from 'lucide-react';
import googleMapsLoader from '../../utils/googleMapsLoader';

const LocationPicker = ({ isOpen, onOpenChange, onLocationSelect }) => {
  const [currentLocation, setCurrentLocation] = useState(null);
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [isLoadingLocation, setIsLoadingLocation] = useState(false);
  const [map, setMap] = useState(null);
  const [isMapLoaded, setIsMapLoaded] = useState(false);
  const [isMapLoading, setIsMapLoading] = useState(false);
  const [mapError, setMapError] = useState(null);

  // Initialize Google Maps when modal opens
  useEffect(() => {
    console.log('LocationPicker: useEffect triggered', {
      isOpen,
      isMapLoaded,
      isMapLoading,
      mapError
    });
    
    // Only load if modal is open and maps not already loaded/loading
    if (isOpen && !isMapLoaded && !isMapLoading) {
      console.log('LocationPicker: Starting Google Maps load process...');
      setIsMapLoading(true);
      setMapError(null);
      
      googleMapsLoader.load()
        .then(() => {
          console.log('LocationPicker: Google Maps loaded successfully');
          // Use functional updates to avoid stale closure issues
          setIsMapLoaded(true);
          setIsMapLoading(false);
        })
        .catch((error) => {
          console.error('LocationPicker: Failed to load Google Maps:', error);
          setMapError(error.message || 'Erro desconhecido ao carregar o mapa');
          setIsMapLoading(false);
        });
    }
    
    // Reset states when modal closes
    if (!isOpen && (isMapLoaded || isMapLoading || mapError)) {
      console.log('LocationPicker: Modal closed, resetting states');
      setIsMapLoaded(false);
      setIsMapLoading(false);
      setMapError(null);
      setMap(null);
      setCurrentLocation(null);
      setSelectedLocation(null);
    }
  }, [isOpen, isMapLoaded, isMapLoading, mapError]);

  // Get current location
  const getCurrentLocation = useCallback(() => {
    setIsLoadingLocation(true);
    
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const location = {
            lat: position.coords.latitude,
            lng: position.coords.longitude
          };
          setCurrentLocation(location);
          setSelectedLocation(location);
          setIsLoadingLocation(false);
        },
        (error) => {
          console.error('Error getting location:', error);
          // Default to a central location if geolocation fails
          const defaultLocation = { lat: -23.5505, lng: -46.6333 }; // São Paulo
          setCurrentLocation(defaultLocation);
          setSelectedLocation(defaultLocation);
          setIsLoadingLocation(false);
        }
      );
    } else {
      console.error('Geolocation is not supported');
      const defaultLocation = { lat: -23.5505, lng: -46.6333 }; // São Paulo
      setCurrentLocation(defaultLocation);
      setSelectedLocation(defaultLocation);
      setIsLoadingLocation(false);
    }
  }, []);

  // Initialize map when modal opens
  useEffect(() => {
    if (isOpen && isMapLoaded && !currentLocation) {
      getCurrentLocation();
    }
  }, [isOpen, isMapLoaded, currentLocation, getCurrentLocation]);

  // Initialize Google Map
  useEffect(() => {
    if (isMapLoaded && currentLocation && !map && !mapError) {
      const mapElement = document.getElementById('location-picker-map');
      if (mapElement && window.google && window.google.maps) {
        try {
          console.log('LocationPicker: Creating Google Map instance...');
          const googleMap = new window.google.maps.Map(mapElement, {
            center: currentLocation,
            zoom: 15,
            mapTypeControl: false,
            streetViewControl: false,
            fullscreenControl: false
          });

          // Add click listener to map
          googleMap.addListener('click', (event) => {
            const clickedLocation = {
              lat: event.latLng.lat(),
              lng: event.latLng.lng()
            };
            setSelectedLocation(clickedLocation);
          });

          console.log('LocationPicker: Google Map created successfully');
          setMap(googleMap);
        } catch (error) {
          console.error('LocationPicker: Error creating map:', error);
          setMapError('Erro ao criar o mapa');
        }
      }
    }
  }, [isMapLoaded, currentLocation, map, mapError]);

  // Update marker when selected location changes
  useEffect(() => {
    if (map && selectedLocation && window.google && window.google.maps) {
      try {
        // Clear existing markers
        if (window.currentMarker) {
          window.currentMarker.setMap(null);
        }

        // Add new marker
        const marker = new window.google.maps.Marker({
          position: selectedLocation,
          map: map,
          title: 'Localização do Incidente',
          icon: {
            url: 'data:image/svg+xml;charset=UTF-8,' + encodeURIComponent(`
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M21 10C21 17 12 23 12 23S3 17 3 10C3 5.02944 7.02944 1 12 1C16.9706 1 21 5.02944 21 10Z" fill="#dc2626" stroke="#ffffff" stroke-width="2"/>
                <circle cx="12" cy="10" r="3" fill="#ffffff"/>
              </svg>
            `),
            scaledSize: new window.google.maps.Size(24, 24),
            anchor: new window.google.maps.Point(12, 24)
          }
        });

        window.currentMarker = marker;

        // Center map on selected location
        map.setCenter(selectedLocation);
      } catch (error) {
        console.error('LocationPicker: Error creating marker:', error);
      }
    }
  }, [map, selectedLocation]);

  const handleConfirm = () => {
    if (selectedLocation && onLocationSelect) {
      onLocationSelect(selectedLocation);
    }
    onOpenChange(false);
  };

  const handleCancel = () => {
    setSelectedLocation(currentLocation);
    onOpenChange(false);
  };

  return (
    <Modal 
      isOpen={isOpen} 
      onOpenChange={onOpenChange}
      size="3xl"
      scrollBehavior="inside"
      backdrop="opaque"
      classNames={{
        backdrop: "bg-black/50",
        base: "border-none",
        wrapper: "p-4",
        body: "py-6",
        header: "border-b-[1px] border-gray-200",
        footer: "border-t-[1px] border-gray-200",
        closeButton: "hover:bg-gray-100"
      }}
    >
      <ModalContent className="bg-white shadow-2xl rounded-nextui-lg">
        <ModalHeader className="flex flex-col gap-1 bg-white">
          <div className="flex items-center">
            <MapPin className="w-5 h-5 mr-2 text-red-600" />
            <span className="text-lg font-semibold text-gray-900">Selecionar Localização do Incidente</span>
          </div>
          <p className="text-sm text-gray-600 font-normal">
            Clique no mapa para marcar onde o incidente de segurança ocorreu
          </p>
        </ModalHeader>
        
        <ModalBody className="bg-white">
          <div className="space-y-4">
            {/* Current Location Button */}
            <div className="flex justify-center">
              <Button
                color="primary"
                variant="bordered"
                startContent={<Navigation className="w-4 h-4" />}
                onPress={getCurrentLocation}
                isLoading={isLoadingLocation}
                size="sm"
                className="bg-white border-2 border-primary-300 hover:border-primary-500"
              >
                Usar Minha Localização Atual
              </Button>
            </div>

            {/* Map Container */}
            <Card className="bg-white border-2 border-gray-200 shadow-lg rounded-nextui">
              <CardBody className="p-0">
                <div 
                  id="location-picker-map" 
                  className="w-full h-96 rounded-lg bg-white"
                  style={{ minHeight: '384px' }}
                >
                  {mapError ? (
                    <div className="flex items-center justify-center h-full bg-red-50 rounded-lg border-2 border-red-200">
                      <div className="text-center p-8">
                        <MapPin className="w-12 h-12 text-red-500 mx-auto mb-4" />
                        <p className="text-red-600 font-medium text-lg mb-2">Erro ao carregar o mapa</p>
                        <p className="text-red-500 text-sm">{mapError}</p>
                        <Button 
                          size="sm" 
                          color="primary" 
                          variant="bordered"
                          className="mt-4"
                          onPress={() => {
                            setMapError(null);
                            setIsMapLoaded(false);
                            setIsMapLoading(false);
                          }}
                        >
                          Tentar Novamente
                        </Button>
                      </div>
                    </div>
                  ) : (!isMapLoaded || isMapLoading) ? (
                    <div className="flex items-center justify-center h-full bg-gray-50 rounded-lg border-2 border-gray-200">
                      <div className="text-center p-8">
                        <div className="animate-spin rounded-full h-12 w-12 border-b-4 border-primary mx-auto mb-4"></div>
                        <p className="text-gray-700 font-medium text-lg">
                          {isMapLoading ? 'Carregando mapa...' : 'Inicializando mapa...'}
                        </p>
                        <p className="text-gray-500 text-sm mt-2">
                          Aguarde enquanto carregamos o Google Maps
                        </p>
                      </div>
                    </div>
                  ) : null}
                </div>
              </CardBody>
            </Card>

            {/* Selected Location Info */}
            {selectedLocation && (
              <Card className="bg-blue-50 border-2 border-blue-200 shadow-sm rounded-nextui">
                <CardBody className="p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-semibold text-blue-800 mb-1">
                        📍 Localização Selecionada
                      </p>
                      <p className="text-xs text-blue-600 font-mono bg-blue-100 px-2 py-1 rounded">
                        Lat: {selectedLocation.lat.toFixed(6)}, Lng: {selectedLocation.lng.toFixed(6)}
                      </p>
                    </div>
                    <div className="text-blue-600">
                      <MapPin className="w-6 h-6" />
                    </div>
                  </div>
                </CardBody>
              </Card>
            )}
          </div>
        </ModalBody>
        
        <ModalFooter className="bg-white">
          <Button 
            color="danger" 
            variant="light" 
            onPress={handleCancel}
            className="bg-white border-2 border-red-300 text-red-600 hover:bg-red-50 hover:border-red-400"
          >
            Cancelar
          </Button>
          <Button 
            color="primary" 
            onPress={handleConfirm}
            isDisabled={!selectedLocation}
            className="bg-primary text-white border-2 border-primary hover:bg-primary-600 disabled:opacity-50"
          >
            Confirmar Localização
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default LocationPicker;
